import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Import note (audit-referentie: Ronde 10): `Children` is NOT used in this
// file. Regressed in ronde 6 and ronde 8 — see `.eslintrc.json` for the
// lint rule that should prevent this once lint is wired into CI.
import { DjayAvatar, DjayBubble, djayVariants, getDjayMissionHeading, getDjayLabWelcome, getDjayModeIntro } from '.';
import type { DjaySize, DjayEmotion, DjayTier, DjayVariant, BubbleColorScheme } from '.';
const SIZES: {
  size: DjaySize;
  label: string;
  pixels: number;
}[] = [{
  size: 'xs',
  label: 'XS',
  pixels: 16
}, {
  size: 'sm',
  label: 'SM',
  pixels: 24
}, {
  size: 'md',
  label: 'MD',
  pixels: 32
}, {
  size: 'lg',
  label: 'LG',
  pixels: 48
}, {
  size: 'xl',
  label: 'XL',
  pixels: 64
}, {
  size: 'hero',
  label: 'Hero',
  pixels: 128
}];
const EMOTIONS: {
  emotion: DjayEmotion;
  label: string;
  icon: string;
}[] = [{
  emotion: 'happy',
  label: 'Happy',
  icon: '😊'
}, {
  emotion: 'excited',
  label: 'Excited',
  icon: '🤩'
}, {
  emotion: 'encouraging',
  label: 'Encouraging',
  icon: '🤗'
}, {
  emotion: 'celebrating',
  label: 'Celebrating',
  icon: '🎉'
}, {
  emotion: 'proud',
  label: 'Proud',
  icon: '🌟'
}, {
  emotion: 'thinking',
  label: 'Thinking',
  icon: '🤔'
}, {
  emotion: 'sleeping',
  label: 'Sleeping',
  icon: '😴'
}, {
  emotion: 'pointing',
  label: 'Pointing',
  icon: '👉'
}, {
  emotion: 'surprised',
  label: 'Surprised',
  icon: '😲'
}];
const TIERS: {
  tier: DjayTier;
  label: string;
  icon: string;
}[] = [{
  tier: 'rookie',
  label: 'Rookie',
  icon: '🌱'
}, {
  tier: 'pro',
  label: 'Pro',
  icon: '🌿'
}, {
  tier: 'master',
  label: 'Master',
  icon: '👑'
}];
const VARIANTS: {
  variant: DjayVariant;
  label: string;
}[] = [{
  variant: 'default',
  label: 'Default'
}, {
  variant: 'blaze',
  label: 'Blaze'
}, {
  variant: 'frost',
  label: 'Frost'
}, {
  variant: 'pixel',
  label: 'Pixel'
}, {
  variant: 'royal',
  label: 'Royal'
}, {
  variant: 'cosmic',
  label: 'Cosmic'
}, {
  variant: 'shadow',
  label: 'Shadow'
}, {
  variant: 'goud',
  label: 'Goud ✨'
}, {
  variant: 'rainbow',
  label: 'Regenboog 🌈'
}];
const BUBBLE_SCHEMES: {
  scheme: BubbleColorScheme;
  label: string;
}[] = [{
  scheme: 'default',
  label: 'Default'
}, {
  scheme: 'indigo',
  label: 'Indigo'
}, {
  scheme: 'amber',
  label: 'Amber'
}, {
  scheme: 'emerald',
  label: 'Emerald'
}, {
  scheme: 'violet',
  label: 'Violet'
}];
const MODE_IDS = ['fluency', 'sprint', 'challenge', 'experiments', 'assessment'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
export function DjayShowcase() {
  const [activeSize, setActiveSize] = useState<DjaySize>('hero');
  const [activeEmotion, setActiveEmotion] = useState<DjayEmotion>('happy');
  const [activeTier, setActiveTier] = useState<DjayTier>('rookie');
  const [activeVariant, setActiveVariant] = useState<DjayVariant>('default');
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const currentSizeConfig = SIZES.find(s => s.size === activeSize);
  const currentVariantConfig = VARIANTS.find(v => v.variant === activeVariant);
  const currentEmotionConfig = EMOTIONS.find(e => e.emotion === activeEmotion);
  const currentTierConfig = TIERS.find(t => t.tier === activeTier);
  return <div className="h-full overflow-y-auto bg-slate-50" data-id="element-3598" data-mp="3d40pw4golr1c">
      <motion.div className="max-w-6xl mx-auto p-6 pb-16 space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-3599" data-mp="391dww089y02k">
        
        {/* Section 1: Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" data-id="element-3600" data-mp="setilk83c5vv">
          
          <div className="flex items-center gap-5" data-id="element-3601" data-mp="1jn4rzzpjumah">
            <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
            repeat: Infinity,
            repeatDelay: 3
          }} data-id="element-3602" data-mp="1nm583agn7ats">
              
              <DjayAvatar size="xl" emotion="happy" animate={animationsEnabled} data-id="element-3603" data-mp="1xl32gu0r1ykp" />
              
            </motion.div>
            <div data-id="element-3604" data-mp="22kzad4ckia5j">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3605" data-mp="22jeffdmccp75">
                Mascotte Djay
              </h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-3606" data-mp="26k84wbffmc25">
                Beheer en preview van de Djay mascotte componenten.
              </p>
            </div>
          </div>
          <button onClick={() => setAnimationsEnabled(!animationsEnabled)} className={`text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap ${animationsEnabled ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3607" data-mp="2gaj79tjgwd6x">
            
            {animationsEnabled ? '✨ Animaties aan' : '⏸ Animaties uit'}
          </button>
        </motion.div>

        {/* Section 2: Unified Configurator */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3608" data-mp="4o91m0gk545p">
          
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3609" data-mp="3vvlh8b1sl9ji">
            <h2 className="font-semibold text-gray-900" data-id="element-3610" data-mp="xcmyudztnwg7">Configurator</h2>
          </div>

          <div className="p-6 flex flex-col lg:flex-row gap-8" data-id="element-3611" data-mp="sdtjsh4vzxdx">
            {/* Controls */}
            <div className="flex-1 space-y-6" data-id="element-3612" data-mp="sg60ys2tm3px">
              {/* Emotion Picker */}
              <div data-id="element-3613" data-mp="nl0ffudbyr2d">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3614" data-mp="io06nbp3ja7k">
                  Emotie
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3615" data-mp="1g3xn0ne5xv4e">
                  {EMOTIONS.map(({
                  emotion,
                  label,
                  icon
                }) => <button key={emotion} onClick={() => setActiveEmotion(emotion)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeEmotion === emotion ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3616" data-mp="1bauos1yfxxhv">
                    
                      <span data-id="element-3617" data-mp="30t9qrr8bgucy">{icon}</span>
                      <span data-id="element-3618" data-mp="30s8e5ibnjzt4">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Tier Picker */}
              <div data-id="element-3619" data-mp="2qvn60o4c68l2">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3620" data-mp="2qvvnktbem22l">Tier</h3>
                <div className="inline-flex bg-gray-100 p-1 rounded-xl" data-id="element-3621" data-mp="2cw41046tndcz">
                  {TIERS.map(({
                  tier,
                  label,
                  icon
                }) => <button key={tier} onClick={() => setActiveTier(tier)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTier === tier ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3622" data-mp="2hws25iorlakx">
                    
                      <span data-id="element-3623" data-mp="20jiizaa17igt">{icon}</span>
                      <span data-id="element-3624" data-mp="1vn1q62rw0fxe">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Variant Picker */}
              <div data-id="element-3625" data-mp="2acqxyguhji6n">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3626" data-mp="2ad8z9rw08mlf">
                  Variant (Skin)
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3627" data-mp="2jbkz8kirgwqj">
                  {VARIANTS.map(({
                  variant,
                  label
                }) => <button key={variant} onClick={() => setActiveVariant(variant)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeVariant === variant ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3628" data-mp="2ecrmwvf7nbry">
                    
                      {label}
                    </button>)}
                </div>
              </div>

              {/* Size Picker */}
              <div data-id="element-3629" data-mp="840gqz3b05ix">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3630" data-mp="84ijmekh1hzt">
                  Formaat
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3631" data-mp="d2225qo7cgxk">
                  {SIZES.map(({
                  size,
                  label,
                  pixels
                }) => <button key={size} onClick={() => setActiveSize(size)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeSize === size ? 'bg-slate-800 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3632" data-mp="msn6luqk4foe">
                    
                      {label}{' '}
                      <span className="opacity-70 font-normal" data-id="element-3633" data-mp="3v8gly695jl3v">
                        ({pixels}px)
                      </span>
                    </button>)}
                </div>
              </div>
            </div>

            {/* Active Preview */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-dashed border-orange-200 p-8 min-w-[300px] lg:w-1/3" data-id="element-3634" data-mp="1akk9uxfxxzdw">
              <div className="flex-1 flex items-center justify-center min-h-[160px]" data-id="element-3635" data-mp="2z47teklrf0f4">
                <AnimatePresence mode="wait" data-id="element-3636" data-mp="2v1bilg2go8lq">
                  <motion.div key={`${activeSize}-${activeEmotion}-${activeTier}-${activeVariant}`} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0.8,
                  opacity: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25
                }} className="flex items-center justify-center" data-id="element-3637" data-mp="2q99yx92yrflo">
                    
                    <DjayAvatar size={activeSize} emotion={activeEmotion} tier={activeTier} variant={activeVariant} animate={animationsEnabled} data-id="element-3638" data-mp="22dxl9guupb13" />
                    
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="mt-6 text-center bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-orange-100 shadow-sm" data-id="element-3639" data-mp="2xq5d732yetkb">
                <div className="text-sm font-bold text-gray-800" data-id="element-3640" data-mp="2srkcl2jsi7vc">
                  {currentTierConfig?.label} · {currentVariantConfig?.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5" data-id="element-3641" data-mp="31sa61zlkvm5w">
                  {currentEmotionConfig?.label} · {currentSizeConfig?.label} (
                  {currentSizeConfig?.pixels}px)
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 3: Matrix Overview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3642" data-mp="2nubs2vb65rqx">
          
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-3643" data-mp="1vcg8f7cgb4o1">
            <div data-id="element-3644" data-mp="1qdd1fhcwuodf">
              <h2 className="font-semibold text-gray-900" data-id="element-3645" data-mp="1qdwnna9lfyx9">
                Tier × Variant Matrix
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-3646" data-mp="5u9l9fi9dn6s">
                Klik op een cel om deze in de configurator te laden. Toont
                huidige emotie: {currentEmotionConfig?.icon}{' '}
                {currentEmotionConfig?.label}.
              </p>
            </div>
          </div>
          <div className="p-6 overflow-x-auto" data-id="element-3647" data-mp="1usfekyx6o13">
            <div className="min-w-[800px]" data-id="element-3648" data-mp="1rcxzbhrx2wi">
              {/* Header Row */}
              <div className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 mb-2" data-id="element-3649" data-mp="pjtjxx4mtf13">
                <div data-id="element-3650" data-mp="tjlswaxxtn6z"></div>
                {VARIANTS.map(v => <div key={v.variant} className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3651" data-mp="tikdju9w2ejg">
                  
                    {v.label}
                  </div>)}
              </div>

              {/* Tier Rows */}
              <div className="space-y-2" data-id="element-3652" data-mp="30abgd8jqufzt">
                {TIERS.map(t => <div key={t.tier} className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 items-center" data-id="element-3653" data-mp="35ag15aropwvu">
                  
                    <div className="text-sm font-semibold text-gray-700 flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-3654" data-mp="2l8l58iw71t09">
                      <span data-id="element-3655" data-mp="2h8uj4shm4122">{t.icon}</span>
                      <span data-id="element-3656" data-mp="2cfhtqg53zq89">{t.label}</span>
                    </div>
                    {VARIANTS.map(v => {
                  const isSelected = activeTier === t.tier && activeVariant === v.variant;
                  return <button key={`${t.tier}-${v.variant}`} onClick={() => {
                    setActiveTier(t.tier);
                    setActiveVariant(v.variant);
                  }} className={`flex justify-center items-center p-3 rounded-xl border transition-all ${isSelected ? 'bg-orange-50 border-orange-400 ring-2 ring-orange-400 ring-offset-1' : 'bg-white border-gray-100 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3657" data-mp="22i3rpljxn3zh">
                        
                          <DjayAvatar size="md" emotion={activeEmotion} tier={t.tier} variant={v.variant} animate={animationsEnabled} data-id="element-3658" data-mp="3nsu3f7njv32c" />
                        
                        </button>;
                })}
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 4: Animations & Bubbles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3659" data-mp="4130xjdzrvv6">
          {/* Animations */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3660" data-mp="8zecpznukgg1">
            
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3661" data-mp="37iuz5fiq1c1t">
              <h2 className="font-semibold text-gray-900" data-id="element-3662" data-mp="3cgmoq2vw8m2u">
                Animaties (Framer Motion)
              </h2>
            </div>
            <div className="p-6 grid grid-cols-3 gap-4" data-id="element-3663" data-mp="3hh0vlhlrdpza">
              <div className="text-center" data-id="element-3664" data-mp="3lhksck2f6rkw">
                <div className="bg-green-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3665" data-mp="3le6spjl54wh5">
                  <motion.div variants={djayVariants.bounce} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3666" data-mp="wr1w7wis4p8a">
                    
                    <DjayAvatar size="lg" emotion="excited" animate={false} data-id="element-3667" data-mp="ua23xweiyaak" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3668" data-mp="197kzpipmdnri">Bounce</div>
              </div>
              <div className="text-center" data-id="element-3669" data-mp="147ymvadpe9j8">
                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3670" data-mp="146x2gfassaou">
                  <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3671" data-mp="1i5wmf9nd81eo">
                    
                    <DjayAvatar size="lg" emotion="celebrating" animate={false} data-id="element-3672" data-mp="1rznawejhsbud" />
                    
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3673" data-mp="20zin8mosacvz">
                  Celebrate
                </div>
              </div>
              <div className="text-center" data-id="element-3674" data-mp="1wusw1b52lmhe">
                <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3675" data-mp="2fkxxmou5us7v">
                  <motion.div variants={djayVariants.tierEvolution} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3676" data-mp="2kkiv0utomsih">
                    
                    <DjayAvatar size="lg" emotion="surprised" animate={false} data-id="element-3677" data-mp="2bi9oeg1jbjad" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3678" data-mp="1wslljadpgckj">
                  Evolution
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bubbles */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3679" data-mp="1nrx9umbsaoat">
            
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3680" data-mp="1dvkelk0lc2dy">
              <h2 className="font-semibold text-gray-900" data-id="element-3681" data-mp="1dy6ovrev8vzv">Speech Bubbles</h2>
            </div>
            <div className="p-6 flex flex-col gap-3" data-id="element-3682" data-mp="1iwrobomanbn9">
              {BUBBLE_SCHEMES.map(({
              scheme,
              label
            }) => <div key={scheme} className="flex items-center gap-4" data-id="element-3683" data-mp="3ft4d7mtlsx9b">
                  <div className="w-20 text-xs font-bold text-gray-500 uppercase tracking-wider text-right" data-id="element-3684" data-mp="3brsm1cv303z0">
                    {label}
                  </div>
                  <div className="flex-1" data-id="element-3685" data-mp="36u98yga4dx8n">
                    <DjayBubble message={`Dit is de ${label.toLowerCase()} bubble variant.`} colorScheme={scheme} autoDismissMs={0} position="bottom" className="w-full" data-id="element-3686" data-mp="7daxvbb0drtu" />
                  
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Section 5: Messages Preview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3687" data-mp="3pi4g3hb88css">
          
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3688" data-mp="2j10u4rl1u1hh">
            <h2 className="font-semibold text-gray-900" data-id="element-3689" data-mp="2e2zdsef0cg9p">
              Berichten Registry Preview
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3690" data-mp="23xzchuco5sn4">
            <div data-id="element-3691" data-mp="28zygn6g8gtl4">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3692" data-mp="28zgczor8rd36">
                Mission Heading
              </div>
              <div className="flex flex-col gap-2" data-id="element-3693" data-mp="2ioq7tcdgz9uz">
                {Array.from({
                length: 3
              }).map((_, i) => <div key={i} className="bg-slate-50 text-slate-800 text-sm px-3 py-2 rounded-lg border border-slate-200" data-id="element-3694" data-mp="2mpjts07cqel8" data-mpi={i}>
                  
                    {getDjayMissionHeading()}
                  </div>)}
              </div>
            </div>

            <div data-id="element-3695" data-mp="2wmwvy0sm9vhg">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3696" data-mp="31nkioa9tbq5v">
                Lab Welcome
              </div>
              <div className="flex flex-col gap-2" data-id="element-3697" data-mp="12c1juoi83y9x">
                {Array.from({
                length: 3
              }).map((_, i) => {
                const msg = getDjayLabWelcome();
                return <div key={i} className="flex items-center gap-2 bg-indigo-50 text-indigo-800 text-sm px-3 py-2 rounded-lg border border-indigo-100" data-id="element-3698" data-mp="39627dnq9fbjf" data-mpi={i}>
                      
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} data-id="element-3699" data-mp="3ixg4xpyni19x" />
                      
                      <span data-id="element-3700" data-mp="3dz4sr8kvveoj">{msg.text}</span>
                    </div>;
              })}
              </div>
            </div>

            <div data-id="element-3701" data-mp="2q9101w98gzcb">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3702" data-mp="2qaln70t7xg05">
                Mode Intros
              </div>
              <div className="flex flex-col gap-2" data-id="element-3703" data-mp="2z9p7g844g1g8">
                {MODE_IDS.slice(0, 3).map(modeId => {
                const msg = getDjayModeIntro(modeId);
                return <div key={modeId} className="flex items-start gap-2 bg-amber-50 text-amber-900 text-sm px-3 py-2 rounded-lg border border-amber-100" data-id="element-3704" data-mp="2v9yfw26rthan">
                      
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} className="mt-0.5 shrink-0" data-id="element-3705" data-mp="ah3hepg7jltm" />
                      
                      <div data-id="element-3706" data-mp="mzpc2yzxsjj5">
                        <span className="font-bold text-[10px] uppercase block text-amber-700 mb-0.5" data-id="element-3707" data-mp="n10d4r1zzd6k">
                          {modeId}
                        </span>
                        {msg.text}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}